import { success } from "App/popupMessage";
import EdgeSource from "Areas/SharedFeatures/types/edgeSource";
import NodeSource from "Areas/SharedFeatures/types/nodeSource";
import getAllDocuments from "Areas/Sidebar/Documents/redux/getAllDocuments";
import { uuid7 } from "Common/uuid7";
import compact from "lodash/compact";
import find from "lodash/find";
import join from "lodash/join";
import keyBy from "lodash/keyBy";
import sortBy from "lodash/sortBy";
import { v4 as uuid } from "uuid";
import actionCreatorsDocumentObjectsMap from "./actionCreators.documentObjectsMap";
import locale from "./actionCreators.locale";
import actionTypes from "./actionTypes";
import api from "./api";
import { processSaveGraph } from "./utils";
const abortControllers = {};
export default {
    getDocumentGraphData: (documentId, completed) => ({
        type: actionTypes.GET_DOCUMENT_GRAPH_DATA,
        payload: {
            promise: () => api.getDocumentGraphData(documentId)
        },
        meta: { documentId },
        completed,
        scope: `${actionTypes.GET_DOCUMENT_GRAPH_DATA}_${documentId}`
    }),
    getDocumentGraphDataUpdates: (documentId) => {
        const scope = `${actionTypes.GET_DOCUMENT_GRAPH_DATA_UPDATES}_${documentId}`;
        if (abortControllers[scope])
            abortControllers[scope].abort();
        abortControllers[scope] = new AbortController();
        return {
            type: actionTypes.GET_DOCUMENT_GRAPH_DATA_UPDATES,
            payload: {
                promise: () => api.getDocumentGraphDataUpdates(documentId, abortControllers[scope])
            },
            meta: { documentId },
            scope
        };
    },
    saveDocumentGraphData: ({ completed, ...data }) => (dispatch, getState) => {
        const document = find(getAllDocuments(getState()), ["Id", data.documentId]);
        if (!document) {
            return undefined;
        }
        const { documentGraphData, graphDataDiff } = processSaveGraph(data);
        return dispatch({
            type: actionTypes.SAVE_DOCUMENT_GRAPH_DATA,
            payload: {
                promise: () => api.saveDocumentGraphData(graphDataDiff)
            },
            meta: {
                documentId: data.documentId,
                sourceData: documentGraphData
            },
            scope: `${actionTypes.SAVE_DOCUMENT_GRAPH_DATA}_${data.documentId}`,
            completed: (disp) => {
                disp(success(locale.buildDocumentSavedSuccessfully(document.Name)));
                completed === null || completed === void 0 ? void 0 : completed();
            },
            rejectedMessage: locale.buildDocumentSavingFailed(document.Name)
        });
    },
    setGraphModel: (documentId, data, updateDraftData = true, updateSourceData = false, updateNodesSourceDocumentObjectId = false) => ({
        type: actionTypes.SET_GRAPH_MODEL,
        documentId,
        updateNodesSourceDocumentObjectId,
        updateDraftData,
        updateSourceData,
        data: {
            ...data,
            edgesSource: data.edgesSource ? compact(data.edgesSource) : data.edgesSource
        }
    }),
    clearData: (documentId) => ({
        type: actionTypes.CLEAR_DATA,
        documentId
    }),
    setDetailedPanelOpen: (detailedPanelOpen) => ({
        type: actionTypes.SET_DETAILED_PANEL_OPEN,
        detailedPanelOpen
    }),
    setSaveTriggered: (documentId, saveTriggered) => ({
        type: actionTypes.SET_SAVE_TRIGGERED,
        documentId,
        saveTriggered
    }),
    clearFromUpdates: (documentId, nodesSourceUpdateRecordIds, edgesSourceUpdateRecordIds) => ({
        type: actionTypes.CLEAR_FROM_UPDATES,
        documentId,
        nodesSourceUpdateRecordIds,
        edgesSourceUpdateRecordIds
    }),
    toggleShowHideTaskResults: (documentId, sourceTaskIds, show) => ({
        type: actionTypes.TOGGLE_SHOW_HIDE_TASK_RESULTS,
        documentId,
        sourceTaskIds,
        show
    }),
    clearFromShowHideTaskResults: (documentId, sourceTaskIds) => ({
        type: actionTypes.CLEAR_FROM_SHOW_HIDE_TASK_RESULTS,
        documentId,
        sourceTaskIds
    }),
    getSourceTasksResults: (documentId, sourceTaskIds) => ({
        type: actionTypes.GET_SOURCE_TASKS_RESULTS,
        payload: {
            promise: () => api.getSourceTasksResults(sourceTaskIds)
        },
        meta: { documentId, sourceTaskIds },
        scope: `${actionTypes.GET_SOURCE_TASKS_RESULTS}_${documentId}`
    }),
    getGraphGroupsNodes: (documentId, groupIdArr, completed, rejected) => ({
        type: actionTypes.GET_GRAPH_GROUPS_NODES,
        payload: {
            promise: () => api.getGraphGroupsNodes(documentId, groupIdArr)
        },
        meta: { documentId, groupIdArr },
        scope: `${actionTypes.GET_GRAPH_GROUPS_NODES}_${documentId}_${join(sortBy(groupIdArr), "_")}`,
        completed: (_, { payload }) => {
            (payload === null || payload === void 0 ? void 0 : payload.NodesSource) &&
                (payload === null || payload === void 0 ? void 0 : payload.EdgesSource) &&
                completed &&
                completed(payload.NodesSource, payload.EdgesSource);
        },
        rejected: () => {
            rejected && rejected();
        }
    }),
    pasteDocumentGraphData: (data, completed, rejected) => ({
        type: actionTypes.PASTE_DOCUMENT_GRAPH_DATA,
        payload: {
            promise: () => api.pasteDocumentGraphData(data)
        },
        scope: `${actionTypes.PASTE_DOCUMENT_GRAPH_DATA}_${uuid()}}`,
        completed: (_, { payload }) => {
            if (!(payload === null || payload === void 0 ? void 0 : payload.NodesSource) || !completed)
                return;
            const time = new Date().getTime();
            completed({
                NodesSource: keyBy(payload.NodesSource, (e) => `${time}_${e.NodeId}`),
                EdgesSource: keyBy(payload.EdgesSource, (e) => `${time}_${e.EdgeId}`),
                Groups: keyBy(payload.Groups, "Id")
            });
        },
        rejected: () => {
            rejected && rejected();
        }
    }),
    setHasChanges: (documentId, hasChanges) => ({
        type: actionTypes.SET_HAS_CHANGES,
        documentId,
        hasChanges
    }),
    setLayoutAlgorithm: (documentId, layoutAlgorithm) => ({
        type: actionTypes.SET_LAYOUT_ALGORITHM,
        documentId,
        layoutAlgorithm
    }),
    setTableModalState: (isOpen) => ({
        type: actionTypes.SET_GROUP_TABLE_MODAL_STATE,
        isOpen
    }),
    setGroupTableData: (documentId, data) => ({
        type: actionTypes.SET_GROUP_TABLE_DATA_FOR_TABLE_VIEW,
        documentId,
        data
    }),
    appendTextToUpdates: (documentId, text, documentObjects, fromNodeSourceId, fromNodeSourceGroupId) => (dispatch) => {
        const nodeSource = new NodeSource(uuid7(), documentObjects[actionCreatorsDocumentObjectsMap.PHRASE.DocumentObjectId], undefined, undefined, compact([
            {
                Key: actionCreatorsDocumentObjectsMap.PHRASE.attributes.Phrase,
                Value: text
            }
        ]));
        const edgeSource = fromNodeSourceId
            ? new EdgeSource(uuid7(), fromNodeSourceId, nodeSource.NodeId, fromNodeSourceGroupId)
            : undefined;
        dispatch({
            type: actionTypes.APPEND_TO_UPDATES,
            documentId,
            nodeSource,
            edgeSource
        });
        dispatch(success(locale.SEND_TO_GRAPH_SUCCEED));
    },
    appendPhotoToUpdates: (documentId, url, documentObjects, fromNodeSourceId, fromNodeSourceGroupId) => (dispatch) => {
        const nodeSource = new NodeSource(uuid7(), documentObjects[actionCreatorsDocumentObjectsMap.PHOTO.DocumentObjectId], undefined, undefined, compact([
            {
                Key: actionCreatorsDocumentObjectsMap.PHOTO.attributes.URL,
                Value: url
            }
        ]));
        const edgeSource = fromNodeSourceId
            ? new EdgeSource(uuid7(), fromNodeSourceId, nodeSource.NodeId, fromNodeSourceGroupId)
            : undefined;
        dispatch({
            type: actionTypes.APPEND_TO_UPDATES,
            documentId,
            nodeSource,
            edgeSource
        });
        dispatch(success(locale.SEND_TO_GRAPH_SUCCEED));
    }
};
